import { useApi, useUserInfo } from "@mb-pro-ui/utils";
import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  deleteToken,
  getMessaging,
  getToken,
  onMessage,
  type Messaging,
} from "firebase/messaging";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { usePush } from "../../contexts/PushContexts";

const firebaseConfig = {
  apiKey: "AIzaSyAP739sDhi0RuJevYgS-5lWhGE_tgQO9O4",
  authDomain: "monitoringbook-pro.firebaseapp.com",
  projectId: "monitoringbook-pro",
  storageBucket: "monitoringbook-pro.firebasestorage.app",
  messagingSenderId: "1050757145091",
  appId: "1:1050757145091:web:5a4f0f7763ecae33936436",
};

const vapidKey =
  "BODJfcXnWOdHtnmx-I3Kfv3As-2ckSwmaivakQChvxbqh2QuofeCWuyNVyd3WEstMw6EcyrG1ufJDTF5w7eyaNY";

let app: FirebaseApp, messaging: Messaging;

try {
  app = initializeApp(firebaseConfig);
  messaging = getMessaging(app);
} catch (e) {
  console.error("Firebase initialization error:", e);
}

const USER_DISABLED_KEY = "push-notifications-disabled";

export function usePushNotifications() {
  const [sid] = useUserInfo();
  const { formatMessage } = useIntl();
  const [enabled, setEnabled] = useState(false);
  const { setLastNotification } = usePush();
  const api = useApi();

  // Token regisztrálása a szerveren
  const registerToken = useCallback(
    async (token: string) => {
      if (!api.authenticated || !token) return;

      try {
        await api("/actions/raptor/push-device", {
          method: "POST",
          headers: { "Content-Type": "text/plain" },
          body: token,
        });
      } catch (e) {
        console.error("Token registration failed:", e);
      }
    },
    [api],
  );

  // Token ellenőrzése és enabled state beállítása
  const checkTokenExists = useCallback(async () => {
    if (!sid || !("Notification" in window)) return false;

    if (localStorage.getItem(USER_DISABLED_KEY) === "true") {
      setEnabled(false);
      return false;
    }

    try {
      const token = await getToken(messaging, { vapidKey });
      const hasToken = !!token;

      // Ha van token és permission granted, akkor enabled
      if (hasToken && Notification.permission === "granted") {
        await registerToken(token);
        setEnabled(true);
        return true;
      } else {
        setEnabled(false);
        return false;
      }
    } catch (e) {
      console.error("Token check failed:", e);
      setEnabled(false);
      return false;
    }
  }, [sid, registerToken]);

  // Inicializálás: permission kérés ha default, token ellenőrzés
  useEffect(() => {
    if (!sid || !("Notification" in window)) return;

    const initialize = async () => {
      if (localStorage.getItem(USER_DISABLED_KEY) === "true") {
        setEnabled(false);
        return;
      }

      if (Notification.permission === "default") {
        try {
          const permission = await Notification.requestPermission();
          if (permission === "granted") {
            const token = await getToken(messaging, { vapidKey });
            if (token) {
              await registerToken(token);
              setEnabled(true);
              localStorage.removeItem(USER_DISABLED_KEY);
            }
          }
        } catch (e) {
          console.error("Permission request failed:", e);
        }
      } else {
        checkTokenExists();
      }
    };

    initialize();
  }, [checkTokenExists, registerToken, sid]);

  // Push bekapcsolása
  const enablePush = useCallback(async () => {
    if (!sid || !("Notification" in window)) return false;

    let permission = Notification.permission;
    if (permission === "default") {
      permission = await Notification.requestPermission();
    }

    if (permission !== "granted") return false;

    try {
      const token = await getToken(messaging, { vapidKey });
      if (token) {
        await registerToken(token);
        setEnabled(true);
        localStorage.removeItem(USER_DISABLED_KEY);
        return true;
      }
    } catch (e) {
      console.error("Enable push failed:", e);
    }
    return false;
  }, [registerToken, sid]);

  // Push kikapcsolása
  const disablePush = useCallback(async () => {
    try {
      await deleteToken(messaging);
    } catch (e) {
      console.error("Disable push failed:", e);
    } finally {
      setEnabled(false);
      localStorage.setItem(USER_DISABLED_KEY, "true");
    }
  }, []);

  // Foreground üzenetek kezelése
  useEffect(() => {
    if (!sid || !enabled) return;

    const unsubscribe = onMessage(messaging, (payload) => {
      const type = (payload.data?.type ?? "").trim().toLowerCase();

      if (type !== "dispatch-job.unit-cancelled") {
        enqueueSnackbar(
          `${payload.notification?.title ?? formatMessage({ defaultMessage: "Notification" })}: ${
            payload.notification?.body ?? ""
          }`,
          { variant: "info" },
        );
      }

      setLastNotification({
        type: type,
        unit: payload.data?.unitName,
      });
    });

    return unsubscribe;
  }, [sid, enabled, formatMessage, setLastNotification]);

  return {
    enabled,
    enablePush,
    disablePush,
    checkToken: checkTokenExists,
  };
}
